'use client';

import Link from 'next/link';
import Image from 'next/image';
import { urlForImage } from '@/lib/strapi';
import { StrapiImageSource } from '@/lib/types';

// Define types for blog posts
interface BlogPost {
  _id: number;
  title: string;
  slug: { current: string };
  publishedDate: string;
  coverImage?: StrapiImageSource;
  excerpt: string;
}

// Simple calendar icon component
function CalendarDot({ className = "" }) {
  return (
    <svg className={`w-3.5 h-3.5 ${className}`} fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
      <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
    </svg>
  );
}

// Reusable blog card client component
export default function BlogCard({ post, isFeatured = false }: { post: BlogPost; isFeatured?: boolean }) {
  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Get image URL if it exists
  const imageUrl = post.coverImage ? urlForImage(post.coverImage) : null;
  console.log('Image URL for post:', post.title, imageUrl?.url ? imageUrl.url() : 'none');
  
  // Fallback image if the Strapi image URL is invalid
  const fallbackImageUrl = "/images/blog-placeholder.jpg";
  
  // Ensure the slug is properly encoded for the URL
  const encodedSlug = encodeURIComponent(post.slug.current);

  return (
    <Link 
      href={`/blog/${encodedSlug}`}
      className={`group block h-full transition-all duration-150 ${
        isFeatured 
          ? 'md:grid md:grid-cols-5 rounded-xl overflow-hidden bg-gradient-to-r from-gray-900 to-[#121212] border border-gray-800 hover:border-blue-900 hover:scale-[1.01]' 
          : 'rounded-lg overflow-hidden bg-[#121212] border border-gray-800 hover:border-blue-600 hover:shadow-[0_0_15px_rgba(37,99,235,0.1)] hover:scale-[1.02]'
      }`}
    >
      {/* Image container */}
      <div className={`relative ${isFeatured ? 'h-56 md:h-full md:col-span-2' : 'h-44'}`}>
        {imageUrl && imageUrl.url ? (
          <Image
            src={imageUrl.url()}
            alt={post.title}
            fill
            className="object-cover"
            priority={isFeatured}
            sizes={isFeatured ? "100vw" : "(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"}
            onError={(e) => {
              // If image fails to load, replace with fallback
              const target = e.target as HTMLImageElement;
              console.error(`Image failed to load: ${target.src}`);
              target.src = fallbackImageUrl;
            }}
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-blue-900/40 to-gray-900 flex items-center justify-center">
            <span className="text-3xl font-bold text-white/70">MM</span>
          </div>
        )}
        
        {/* Date overlay on image */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-3">
          <div className="flex items-center text-xs text-gray-300">
            <CalendarDot className="mr-1.5" />
            {post.publishedDate && formatDate(post.publishedDate)}
          </div>
        </div>
      </div>
      
      {/* Content area */}
      <div className={`p-4 flex flex-col h-full justify-between ${isFeatured ? 'md:col-span-3' : ''}`}>
        <div>
          <h3 className={`font-bold group-hover:text-blue-400 transition-colors duration-150 ${
            isFeatured ? 'text-xl md:text-2xl mb-2' : 'text-lg mb-1'
          }`}>
            {post.title}
          </h3>
          
          {/* Show excerpt only for featured posts */}
          {isFeatured && post.excerpt && (
            <p className="text-gray-300 text-sm mb-4 line-clamp-3">{post.excerpt}</p>
          )}
        </div>
        
        {!isFeatured && (
          <div className="flex items-center pt-3 text-xs text-gray-400 mt-2">
            <span className="text-blue-400 font-medium group-hover:underline">Read article</span>
            <svg className="w-3.5 h-3.5 ml-2 transition-transform duration-150 group-hover:translate-x-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
        )}
        
        {isFeatured && (
          <div className="flex items-center pt-3 text-blue-400 font-medium group-hover:underline">
            Read full article
            <svg className="w-4 h-4 ml-2 transition-transform duration-150 group-hover:translate-x-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </div>
        )}
      </div>
    </Link>
  );
} 